// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { color, xScreen } from "@adl/XDL/Utilities";
import baseTheme, { BaseTheme } from "@insite/mobius/globals/baseTheme";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import styled, { css } from "styled-components";

export const FooterContainerWrapper = styled.div`
    background: ${color("primary")};
`;

export const FooterContentWrapper = styled.div`
    flex-grow: 1;
    width: 100%;
    margin: 0 auto;
    ${({ theme }: { theme: BaseTheme }) => {
        const { maxWidths } = theme.breakpoints || baseTheme.breakpoints;
        return breakpointMediaQueries(
            theme,
            maxWidths.map(mw =>
                mw
                    ? css`
                          max-width: ${mw}px;
                      `
                    : null,
            ),
        );
    }}

    @media print {
        max-width: 100%;
    }
`;

export const FooterTopSection = styled.div`
    display: flex;
    justify-content: space-between;
    padding: 24px 35px;
    flex-wrap: nowrap;
    ${xScreen("lte", "sm")} {
        padding: 16px;
    }
    ${xScreen("lte", "xs")} {
        flex-wrap: wrap;
    }
`;

export const FooterBottomSection = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 24px 35px;
    flex-wrap: wrap;
    ${xScreen("lte", "xs")} {
        padding: 16px 16px 56px;
        justify-content: center;
    }
`;

export const FooterRightSection = styled.div`
    display: flex;
    flex-wrap: wrap;
    > div {
        max-width: 200px;
        min-width: 190px;
        ${xScreen("gt", "md")} {
            &:not(:last-child) {
                margin-right: 60px;
            }
        }
        ${xScreen("lte", "md")} {
            margin-right: 16px;
        }
        ${xScreen("lte", "xs")} {
            &:nth-child(odd) {
                margin-right: 12px;
            }
            &:nth-child(even) {
                margin-left: 12px;
                margin-right: 0;
            }
        }
    }
    ${xScreen("lte", "md")} {
        justify-content: space-between;
    }
`;

export const FooterLeftSection = styled.div`
    display: flex;
    flex-direction: column;
    margin-right: 16px;
    p {
        margin-bottom: 0;
    }
    & > div {
        margin-bottom: 16px;
        &:not(:last-child) {
            ${xScreen("gte", "sm")} {
                max-width: 150px;
            }
        }
        &[data-test-selector="email-zone"] {
            margin-bottom: 24px;
        }
    }
    & a:hover span {
        text-decoration: underline;
    }
    ${xScreen("lte", "md")} {
        min-width: 168px;
    }
    ${xScreen("lte", "xs")} {
        flex-direction: row;
        margin-right: 0;
        & > div {
            width: 50%;
            &:first-child {
                margin-right: 24px;
            }
        }
        .social-links > div {
            padding-right: 0;
        }
        .social-link svg {
            width: 25px;
            height: 25px;
        }
        p,
        a {
            line-height: 18px;
        }
    }
    && > .footer-address-wrapper {
        margin-bottom: 8px;
        & > div {
            margin-bottom: 16px;
        }
    }
    && > .footer-social-links-wrapper-desktop {
        margin-bottom: 0;
        & .social-links {
            padding: 0;
        }
    }
`;

export const FooterDivider = styled.div`
    width: 100%;
    height: 1px;
    background: ${color("common.ui4")};
`;

export const FooterSiteMapLinksWrapper = styled.div`
    > div[class^="Wrapper-sc"] {
        display: flex;
        align-items: center;
        > div {
            &:not(:last-child) {
                margin-right: 24px;
            }
        }
        .link {
            > span {
                color: ${color("common.white")};
                font-size: 10px;
                letter-spacing: 0;
                line-height: 15px;
            }
            &:hover {
                > span {
                    text-decoration: underline;
                }
            }
        }
    }
`;

export const GeneralLinksWrapper = styled.div`
    margin-bottom: 20px;
    ${xScreen("lte", "md")} {
        width: calc(50% - 16px);
        &&& {
            max-width: 100%;
        }
    }
    ${xScreen("lte", "xs")} {
        width: calc(50% - 12px);
        &&& {
            min-width: auto;
        }
    }
    .list-wrapper {
        padding: 0;
        .title-wrapper {
            margin-bottom: 16px;
            .list-title {
                > span {
                    font-size: 18px;
                    font-weight: bold;
                    letter-spacing: 0;
                    line-height: 22px;
                    color: ${getColor("common.white")};
                    text-transform: capitalize;
                }
                &:hover {
                    > span {
                        text-decoration: underline;
                    }
                }
            }
        }
        .links-wrapper {
            margin-bottom: 8px;
            ${xScreen("lte", "md")} {
                padding-right: 0;
            }
            .list-link {
                > span {
                    font-size: 12px;
                    letter-spacing: 0;
                    line-height: 18px;
                    font-weight: normal;
                    text-transform: capitalize;
                    color: ${getColor("common.white")};
                }
                &:hover {
                    > span {
                        text-decoration: underline;
                    }
                }
            }
        }
    }
`;

export const CopyRightWrapper = styled.div`
    font-size: 10px;
    letter-spacing: 0;
    line-height: 15px;
    margin-right: 20px;
    color: ${getColor("common.white")};
`;
